import PropTypes from "prop-types"
import React from "react"

// Small line icons for the press page. All are decorative: the text next to
// each one says the same thing, so they are hidden from screen readers.

const Icon = ({ children, size = 24 }) => (
  <svg
    aria-hidden="true"
    fill="none"
    focusable="false"
    height={size}
    stroke="currentColor"
    strokeLinecap="round"
    strokeLinejoin="round"
    strokeWidth="1.5"
    viewBox="0 0 24 24"
    width={size}
  >
    {children}
  </svg>
)

Icon.propTypes = {
  children: PropTypes.node,
  size: PropTypes.number,
}

export const ArrowRight = props => (
  <Icon {...props}>
    <path d="M5 12h14M13 6l6 6-6 6" />
  </Icon>
)

export const ArrowLeft = props => (
  <Icon {...props}>
    <path d="M19 12H5M11 6l-6 6 6 6" />
  </Icon>
)

export const ArrowUpRight = props => (
  <Icon {...props}>
    <path d="M7 17L17 7M8 7h9v9" />
  </Icon>
)

export const ChevronDown = props => (
  <Icon {...props}>
    <path d="M6 9l6 6 6-6" />
  </Icon>
)

// Category tab icons
export const ViewAllIcon = props => (
  <Icon {...props}>
    <circle cx="8" cy="8" r="3" />
    <circle cx="16" cy="8" r="3" />
    <circle cx="8" cy="16" r="3" />
    <circle cx="16" cy="16" r="3" />
  </Icon>
)

export const LeadershipIcon = props => (
  <Icon {...props}>
    <path d="M9 18h6M10 21h4M12 3a6 6 0 00-3.5 10.9c.6.4 1 1.1 1 1.9V16h5v-.2c0-.8.4-1.5 1-1.9A6 6 0 0012 3z" />
  </Icon>
)

export const ExpansionIcon = props => (
  <Icon {...props}>
    <path d="M12 21s-6-5.4-6-10.5a6 6 0 1112 0C18 15.6 12 21 12 21z" />
    <circle cx="12" cy="10.5" r="2.25" />
  </Icon>
)

export const PartnershipsIcon = props => (
  <Icon {...props}>
    <circle cx="9" cy="12" r="5" />
    <circle cx="15" cy="12" r="5" />
  </Icon>
)

export const NewsIcon = props => (
  <Icon {...props}>
    <rect height="16" rx="2" width="16" x="4" y="4" />
    <path d="M8 9h8M8 13h8M8 17h5" />
  </Icon>
)

// Media Inquiries list icons
export const MicrophoneIcon = props => (
  <Icon {...props}>
    <rect height="11" rx="3" width="6" x="9" y="3" />
    <path d="M6 11a6 6 0 0012 0M12 17v4M9 21h6" />
  </Icon>
)

export const HeadphonesIcon = props => (
  <Icon {...props}>
    <path d="M4 15v-3a8 8 0 0116 0v3" />
    <rect height="6" rx="1.5" width="4" x="3" y="14" />
    <rect height="6" rx="1.5" width="4" x="17" y="14" />
  </Icon>
)

export const PresentationIcon = props => (
  <Icon {...props}>
    <rect height="11" rx="1" width="18" x="3" y="4" />
    <path d="M12 15v3M8 21l4-3 4 3M7 11l3-3 2 2 4-4" />
  </Icon>
)

export const ShieldIcon = props => (
  <Icon {...props}>
    <path d="M12 3l7 3v5c0 4.5-3 8.3-7 10-4-1.7-7-5.5-7-10V6l7-3z" />
    <path d="M9 12l2 2 4-4" />
  </Icon>
)

export const FamilyIcon = props => (
  <Icon {...props}>
    <circle cx="8" cy="6.5" r="2.5" />
    <circle cx="16" cy="6.5" r="2.5" />
    <circle cx="12" cy="13" r="2" />
    <path d="M3.5 20v-3.5A3.5 3.5 0 017 13h1.5M20.5 20v-3.5A3.5 3.5 0 0017 13h-1.5M9 20v-1.5a3 3 0 016 0V20" />
  </Icon>
)

// About Cortica service label icons
export const EvaluationsIcon = props => (
  <Icon {...props}>
    <rect height="5" rx="1" width="5" x="3" y="3" />
    <rect height="5" rx="1" width="5" x="16" y="9.5" />
    <rect height="5" rx="1" width="5" x="16" y="16" />
    <path d="M8 5.5h3v13h5M11 12h5" />
  </Icon>
)

export const AbaIcon = props => (
  <Icon {...props}>
    <path d="M14 21v-3h2a2 2 0 002-2v-2.5l1.5-.5-1.5-3.5A7 7 0 005 11c0 2.4 1 4 2.5 5v5" />
    <circle cx="11.5" cy="10" r="2" />
    <path d="M11.5 6.5V8M11.5 12v1.5M8 10h1.5M13.5 10H15" />
  </Icon>
)

export const SpeechIcon = props => (
  <Icon {...props}>
    <path d="M4 5h16v11H9l-5 4V5z" />
    <path d="M9 10.5h.01M12 10.5h.01M15 10.5h.01" strokeWidth="2.5" />
  </Icon>
)

export const OccupationalIcon = props => (
  <Icon {...props}>
    <path d="M3 14h3v7H3zM6 15h3.5l3 1h2a1.5 1.5 0 010 3H11M14.5 19l5-1.8a1.5 1.5 0 011 2.8L13 22.5H6" />
    <path d="M16 11s-4-2.4-4-5a2 2 0 014-.8A2 2 0 0120 6c0 2.6-4 5-4 5z" />
  </Icon>
)

export const MentalHealthIcon = props => (
  <Icon {...props}>
    <path d="M19.5 12.5A8 8 0 1111.5 4" />
    <path d="M8.5 10.5h.01M14.5 10.5h.01" strokeWidth="2.5" />
    <path d="M8.5 14.5a4 4 0 006 0M19 3v5M16.5 5.5h5" />
  </Icon>
)
